import { useCallback, useEffect } from 'react'
import { X, Download, ExternalLink } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'

import { Button } from '@/common/ui/button'

interface ImagePreviewModalProps {
	imageUrl: string | null
	onClose: () => void
}

export function ImagePreviewModal({
	imageUrl,
	onClose,
}: ImagePreviewModalProps) {
	// Close on escape key
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose()
			}
		}

		if (imageUrl) {
			document.addEventListener('keydown', handleKeyDown)
			// Prevent body scroll when modal is open
			document.body.style.overflow = 'hidden'
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			document.body.style.overflow = ''
		}
	}, [imageUrl, onClose])

	const handleBackdropClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget) {
				onClose()
			}
		},
		[onClose],
	)

	const handleDownload = useCallback(() => {
		if (!imageUrl) return

		const link = document.createElement('a')
		link.href = imageUrl
		link.download = `image-${Date.now()}.jpg`
		link.target = '_blank'
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}, [imageUrl])

	const handleOpenInNewTab = useCallback(() => {
		if (imageUrl) {
			window.open(imageUrl, '_blank')
		}
	}, [imageUrl])

	return (
		<AnimatePresence>
			{imageUrl && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
					onClick={handleBackdropClick}
				>
					{/* Close button */}
					<Button
						variant="ghost"
						size="icon"
						className="absolute right-4 top-4 h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70"
						onClick={onClose}
					>
						<X className="h-6 w-6" />
					</Button>

					{/* Action buttons */}
					<div className="absolute left-4 top-4 flex gap-2">
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70"
							onClick={handleDownload}
							title="ดาวน์โหลด"
						>
							<Download className="h-5 w-5" />
						</Button>
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70"
							onClick={handleOpenInNewTab}
							title="เปิดในแท็บใหม่"
						>
							<ExternalLink className="h-5 w-5" />
						</Button>
					</div>

					{/* Image */}
					<motion.img
						initial={{ scale: 0.9, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						exit={{ scale: 0.9, opacity: 0 }}
						transition={{ duration: 0.2 }}
						src={imageUrl}
						alt="Preview"
						className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
						onClick={(e) => e.stopPropagation()}
					/>
				</motion.div>
			)}
		</AnimatePresence>
	)
}

// ==================== Video Preview Modal ====================

interface VideoPreviewModalProps {
	videoUrl: string | null
	onClose: () => void
}

export function VideoPreviewModal({
	videoUrl,
	onClose,
}: VideoPreviewModalProps) {
	// Close on escape key
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose()
			}
		}

		if (videoUrl) {
			document.addEventListener('keydown', handleKeyDown)
			// Prevent body scroll when modal is open
			document.body.style.overflow = 'hidden'
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			document.body.style.overflow = ''
		}
	}, [videoUrl, onClose])

	const handleBackdropClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget) {
				onClose()
			}
		},
		[onClose],
	)

	const handleDownload = useCallback(() => {
		if (!videoUrl) return

		const link = document.createElement('a')
		link.href = videoUrl
		link.download = `video-${Date.now()}.mp4`
		link.target = '_blank'
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}, [videoUrl])

	const handleOpenInNewTab = useCallback(() => {
		if (videoUrl) {
			window.open(videoUrl, '_blank')
		}
	}, [videoUrl])

	return (
		<AnimatePresence>
			{videoUrl && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
					onClick={handleBackdropClick}
				>
					{/* Close button */}
					<Button
						variant="ghost"
						size="icon"
						className="absolute right-4 top-4 h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70"
						onClick={onClose}
					>
						<X className="h-6 w-6" />
					</Button>

					{/* Action buttons */}
					<div className="absolute left-4 top-4 flex gap-2">
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70"
							onClick={handleDownload}
							title="ดาวน์โหลด"
						>
							<Download className="h-5 w-5" />
						</Button>
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70"
							onClick={handleOpenInNewTab}
							title="เปิดในแท็บใหม่"
						>
							<ExternalLink className="h-5 w-5" />
						</Button>
					</div>

					{/* Video */}
					<motion.video
						initial={{ scale: 0.9, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						exit={{ scale: 0.9, opacity: 0 }}
						transition={{ duration: 0.2 }}
						src={videoUrl}
						controls
						autoPlay
						className="max-h-[90vh] max-w-[90vw] rounded-lg"
						onClick={(e) => e.stopPropagation()}
					/>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
